/**
 * "How long ago", in whole days/weeks/months — the bucketed prose a reader scans a list with,
 * not the numeric DD/MM/YYYY the rest of the site uses for a date that matters on its own (see
 * formatDate.ts). A forum topic's last-activity line is exactly the case formatDate.ts says has
 * no place in this design system otherwise: here it is the read the design spec (12B) asked for,
 * not a stand-in for the numeric date.
 */
export type Language = 'ro' | 'en';

function days(iso: string): number {
  const then = new Date(iso).getTime();
  return Math.floor((Date.now() - then) / (24 * 60 * 60 * 1000));
}

const RO = {
  today: 'azi',
  yesterday: 'ieri',
  days: (n: number) => `acum ${n} zile`,
  week: 'acum o săptămână',
  weeks: (n: number) => `acum ${n} săptămâni`,
  month: 'acum o lună',
  months: (n: number) => `acum ${n} luni`,
};

const EN = {
  today: 'today',
  yesterday: 'yesterday',
  days: (n: number) => `${n} days ago`,
  week: 'a week ago',
  weeks: (n: number) => `${n} weeks ago`,
  month: 'a month ago',
  months: (n: number) => `${n} months ago`,
};

/** Buckets: today, yesterday, N days (2-6), a/N week(s), a/N month(s). Anything under 0 (clock
 * skew, a just-created row) reads as "today" rather than a negative count. */
export function relativeTime(iso: string, language: Language): string {
  const words = language === 'ro' ? RO : EN;
  const d = Math.max(0, days(iso));
  if (d === 0) return words.today;
  if (d === 1) return words.yesterday;
  if (d < 7) return words.days(d);
  const weeks = Math.floor(d / 7);
  if (weeks === 1) return words.week;
  if (weeks < 5) return words.weeks(weeks);
  const months = Math.floor(d / 30);
  if (months <= 1) return words.month;
  return words.months(months);
}
